/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useState, useEffect } from "react";
import { Cookie, X, Shield, BarChart3 } from "lucide-react";
import { useTranslation } from "react-i18next";

const CookieConsentBanner = () => {
    const { t } = useTranslation();
    const [showBanner, setShowBanner] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [preferences, setPreferences] = useState({
        necessary: true, // Always true
        analytics: false,
        marketing: false,
        functionality: false
    });

    useEffect(() => {
        // Check if consent has already been given
        const consent = localStorage.getItem("cookieConsent");
        if (!consent) {
            setShowBanner(true);
        } else {
            try {
                // Try to parse as JSON (new format)
                const stored = JSON.parse(consent);
                updateGoogleConsent(stored.type || stored);
            } catch (_error) {
                // Handle old format (plain string like "accepted")
                // If it's a legacy string, treat it as "all" consent
                if (consent === "accepted" || consent === "all") {
                    updateGoogleConsent("all");
                } else if (consent === "necessary") {
                    updateGoogleConsent("necessary");
                } else {
                    // Invalid format, clear and show banner
                    localStorage.removeItem("cookieConsent");
                    setShowBanner(true);
                }
            }
        }
    }, []);

    const updateGoogleConsent = (consentType) => {
        if (typeof window.gtag === 'function') {
            if (consentType === 'all') {
                // Grant all consents
                window.gtag('consent', 'update', {
                    'ad_storage': 'granted',
                    'ad_user_data': 'granted',
                    'ad_personalization': 'granted',
                    'analytics_storage': 'granted',
                    'functionality_storage': 'granted',
                    'personalization_storage': 'granted'
                });
            } else if (consentType === 'necessary') {
                // Only security storage (already granted by default)
                window.gtag('consent', 'update', {
                    'ad_storage': 'denied',
                    'ad_user_data': 'denied',
                    'ad_personalization': 'denied',
                    'analytics_storage': 'denied',
                    'functionality_storage': 'denied',
                    'personalization_storage': 'denied'
                });
            } else if (consentType === 'custom') {
                // Custom preferences
                window.gtag('consent', 'update', {
                    'ad_storage': preferences.marketing ? 'granted' : 'denied',
                    'ad_user_data': preferences.marketing ? 'granted' : 'denied',
                    'ad_personalization': preferences.marketing ? 'granted' : 'denied',
                    'analytics_storage': preferences.analytics ? 'granted' : 'denied',
                    'functionality_storage': preferences.functionality ? 'granted' : 'denied',
                    'personalization_storage': preferences.functionality ? 'granted' : 'denied'
                });
            }
        }
    };

    const handleAccept = (type) => {
        const consentData = {
            type,
            timestamp: new Date().toISOString(),
            preferences: type === 'custom' ? preferences : null
        };

        localStorage.setItem("cookieConsent", JSON.stringify(consentData));
        updateGoogleConsent(type === 'custom' ? 'custom' : type);
        setShowBanner(false);
    };

    const togglePreference = (key) => {
        if (key === 'necessary') return; // Can't disable necessary cookies
        setPreferences(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    if (!showBanner) return null;

    return (
        <div className="fixed w-full bottom-0 z-50 animate-slideUp">
            <div className="w-full">
                <div className="bg-white/98 backdrop-blur-xl shadow-2xl border-t-2 border-blue-500/20 overflow-hidden">
                    {/* Accent Bar */}
                    <div className="h-1 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700"></div>

                    <div className="p-6 md:p-8 max-w-7xl mx-auto">
                        <div className="flex items-start justify-between gap-6">
                            {/* Icon & Content */}
                            <div className="flex gap-4 flex-1">
                                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg ring-4 ring-blue-100">
                                    <Cookie className="w-7 h-7 text-white" />
                                </div>

                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                        {t("Cookie Preferences")}
                                    </h3>
                                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                                        {t("We use cookies to enhance your browsing experience, deliver personalized content, and analyze site traffic. Choose your preferences below or accept all to continue.")}
                                    </p>

                                    {/* Cookie Details Toggle */}
                                    {showDetails && (
                                        <div className="mb-6 space-y-3 bg-gray-50 rounded-xl p-4 border border-gray-200">
                                            {/* Necessary Cookies */}
                                            <div className="flex items-start justify-between gap-4 p-3 bg-white rounded-lg border border-gray-200">
                                                <div className="flex items-start gap-3 flex-1">
                                                    <Shield className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 text-sm">{t("Necessary Cookies")}</h4>
                                                        <p className="text-xs text-gray-600 mt-1">{t("Required for basic site functionality and security.")}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                                                        {t("Always Active")}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Analytics Cookies */}
                                            <div className="flex items-start justify-between gap-4 p-3 bg-white rounded-lg border border-gray-200">
                                                <div className="flex items-start gap-3 flex-1">
                                                    <BarChart3 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 text-sm">{t("Analytics Cookies")}</h4>
                                                        <p className="text-xs text-gray-600 mt-1">{t("Help us understand how visitors interact with our website.")}</p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => togglePreference('analytics')}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${preferences.analytics ? 'bg-blue-600' : 'bg-gray-300'
                                                        }`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preferences.analytics ? 'translate-x-6' : 'translate-x-1'
                                                        }`} />
                                                </button>
                                            </div>

                                            {/* Marketing Cookies */}
                                            <div className="flex items-start justify-between gap-4 p-3 bg-white rounded-lg border border-gray-200">
                                                <div className="flex items-start gap-3 flex-1">
                                                    <Cookie className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 text-sm">{t("Marketing Cookies")}</h4>
                                                        <p className="text-xs text-gray-600 mt-1">{t("Used to deliver personalized ads and track ad performance.")}</p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => togglePreference('marketing')}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${preferences.marketing ? 'bg-blue-600' : 'bg-gray-300'
                                                        }`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preferences.marketing ? 'translate-x-6' : 'translate-x-1'
                                                        }`} />
                                                </button>
                                            </div>

                                            {/* Functionality Cookies */}
                                            <div className="flex items-start justify-between gap-4 p-3 bg-white rounded-lg border border-gray-200">
                                                <div className="flex items-start gap-3 flex-1">
                                                    <Cookie className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 text-sm">{t("Functionality Cookies")}</h4>
                                                        <p className="text-xs text-gray-600 mt-1">{t("Enable enhanced features and personalization.")}</p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => togglePreference('functionality')}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${preferences.functionality ? 'bg-blue-600' : 'bg-gray-300'
                                                        }`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preferences.functionality ? 'translate-x-6' : 'translate-x-1'
                                                        }`} />
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Buttons */}
                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            type="button"
                                            onClick={() => handleAccept("all")}
                                            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 hover:from-blue-700 hover:to-blue-800"
                                        >
                                            {t("Accept All")}
                                        </button>

                                        {showDetails && (
                                            <button
                                                type="button"
                                                onClick={() => handleAccept("custom")}
                                                className="px-6 py-3 bg-blue-100 text-blue-700 font-bold rounded-lg hover:bg-blue-200 transition-all duration-200"
                                            >
                                                {t("Save Preferences")}
                                            </button>
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => setShowDetails(!showDetails)}
                                            className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-all duration-200"
                                        >
                                            {showDetails ? t("Hide Details") : t("Customize")}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => handleAccept("necessary")}
                                            className="px-6 py-3 text-gray-600 font-semibold hover:text-gray-900 transition-all duration-200 underline"
                                        >
                                            {t("Necessary Only")}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Close Button */}
                            <button
                                type="button"
                                onClick={() => setShowBanner(false)}
                                aria-label={t("Close cookie consent banner")}
                                className="flex-shrink-0 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors duration-200 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CookieConsentBanner;
